import mapFunctions from '@/mixins/mapFunctions';
import GeoType from '@/enums/GeoType';
import transformers from '@/mixins/transformers';

const cache = {
  actions: Object.create(null),
  mapObjects: Object.create(null),
  selections: Object.create(null),
  childIdsByParentId: Object.create(null),
  actionIdsByMapObjectId: Object.create(null),
  openActionStatusIds: Object.create(null),
};

export function resetDataCache() {
  cache.actions = Object.create(null);
  cache.mapObjects = Object.create(null);
  cache.selections = Object.create(null);
  cache.childIdsByParentId = Object.create(null);
  cache.actionIdsByMapObjectId = Object.create(null);
  cache.openActionStatusIds = Object.create(null);
}

export function setOpenActionStatusIds(openActionStatusIds) {
  cache.openActionStatusIds = openActionStatusIds;
}

export function getOpenActionStatusIds() {
  return cache.openActionStatusIds;
}

export function upsertChildToParentIndex(mapObject) {
  if (mapObject.parent_id) {
    let childArray = [];
    if (cache.childIdsByParentId[mapObject.parent_id]) {
      childArray = cache.childIdsByParentId[mapObject.parent_id];
    }
    if (childArray.length > 0) {
      childArray = childArray.filter(item => item.id !== mapObject.id);
    }
    childArray.push({
      id: mapObject.id,
      map_object_status_id: mapObject.map_object_status_id,
      map_object_type_id: mapObject.map_object_type_id,
    });
    cache.childIdsByParentId[mapObject.parent_id] = childArray;
  }
}

export function upsertActionToMapObjectIndex(action) {
  if (action.map_object_id) {
    if (!cache.actionIdsByMapObjectId[action.map_object_id]) {
      cache.actionIdsByMapObjectId[action.map_object_id] = [];
    }
    if (cache.actionIdsByMapObjectId[action.map_object_id]
      .filter(item => item.id === action.id).length > 0) {
      cache.actionIdsByMapObjectId[action.map_object_id] = cache.actionIdsByMapObjectId[action.map_object_id]
        .filter(item => item.id !== action.id);
    }
    cache.actionIdsByMapObjectId[action.map_object_id].push({
      id: action.id,
      action_priority_id: action.action_priority_id,
      action_status_id: action.action_status_id,
      dynamic_action_type_id: action.dynamic_action_type_id,
    });
  }
}

export function updateActionMapObjectIndexByMapObjectId(mapObjectId) {
  cache.actionIdsByMapObjectId[mapObjectId] = [];
  const actions = Object.values(cache.actions).filter(item => item.map_object_id === mapObjectId);
  actions.forEach((action) => {
    upsertActionToMapObjectIndex(action);
  });
}

export function updateActionMapObjectIndexByAction(mapObjectId) {
  cache.actionIdsByMapObjectId[mapObjectId] = [];
  const actions = Object.values(cache.actions).filter(item => item.map_object_id === mapObjectId);
  actions.forEach((action) => {
    upsertActionToMapObjectIndex(action);
  });
}

export function setActions(actions = Object.create(null)) {
  cache.actions = actions || Object.create(null);
}

export function getActions() {
  return cache.actions;
}

export function paginateActions(page = 1, sort = 'created_at', sortBy = 'desc', onlyNotFinal = true, filter = {}) {
  const itemsPerPage = 15;
  let actions = Object.values(cache.actions);
  if (onlyNotFinal) {
    actions = actions.filter(item => cache.openActionStatusIds.includes(item.action_status_id));
  }
  if (filter && Object.keys(filter).length > 0) {
    if (filter.actionId) {
      actions = actions.filter(item => item.id === parseInt(filter.actionId, 10));
    }
    if (filter.actionPriority && filter.actionPriority.length > 0) {
      const actionPriorityIds = filter.actionPriority.map(item => item.id);
      actions = actions.filter(item => actionPriorityIds.includes(item.action_priority_id));
    }
    if (filter.actionStatus && filter.actionStatus.length > 0) {
      const actionStatusIds = filter.actionStatus.map(item => item.id);
      actions = actions.filter(item => actionStatusIds.includes(item.action_status_id));
    }
    if (filter.createdBy && filter.createdBy.length > 0) {
      const createdByIds = filter.createdBy.map(item => item.id);
      actions = actions.filter(item => createdByIds.includes(item.created_by));
    }
    if (filter.dynamicActionType && filter.dynamicActionType.length > 0) {
      const dynamicActionTypeIds = filter.dynamicActionType.map(item => item.id);
      actions = actions.filter(item => dynamicActionTypeIds.includes(item.dynamic_action_type_id));
    }
    if (filter.cityDistrict) {
      actions = actions.filter((item) => {
        if ((!item.geoObject || !item.geoObject.data) && !item.city_district) {
          return false;
        }
        if (item.city_district && item.city_district === filter.cityDistrict) {
          return true;
        }
        if (item.geoObject && item.geoObject.data && item.geoObject.data.properties
          && item.geoObject.data.properties.city_district
          && item.geoObject.data.properties.city_district === filter.cityDistrict) {
          return true;
        }
        return false;
      });
    }
    if (filter.fromDate) {
      const [year, month, day] = filter.fromDate.split('-').map(Number);
      const timestamp = Math.floor(
        new Date(year, month - 1, day).getTime() / 1000,
      );
      actions = actions.filter(item => item[filter.date] > timestamp);
    }
    if (filter.toDate) {
      const [year, month, day] = filter.toDate.split('-').map(Number);
      const timestamp = Math.floor(
        new Date(year, month - 1, day).getTime() / 1000,
      );
      actions = actions.filter(item => item[filter.date] < timestamp);
    }
  }
  const direction = sortBy.toLowerCase() === 'asc' ? 1 : -1;

  actions.sort((actionA, actionB) => {
    const valueA = actionA[sort];
    const valueB = actionB[sort];

    // Keep null or undefined values at the end.
    if (valueA === null && valueB === null) { return 0; }
    if (valueA === null) { return 1; }
    if (valueB === null) { return -1; }

    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return valueA.localeCompare(valueB) * direction;
    }

    if (valueA < valueB) { return -1 * direction; }
    if (valueA > valueB) { return 1 * direction; }

    return 0;
  });

  const total = actions.length;
  const lastPage = Math.max(1, Math.ceil(total / itemsPerPage));
  const safePage = Math.min(
    Math.max(1, Number(page) || 1),
    lastPage,
  );
  const startIndex = (safePage - 1) * itemsPerPage;

  return {
    data: actions.slice(startIndex, startIndex + itemsPerPage),
    meta: {
      pagination: {
        total,
        count: Math.min(itemsPerPage, total),
        per_page: itemsPerPage,
        current_page: safePage,
        total_pages: lastPage,
      },
    },
  };
}

export function getActionDistricts() {
  const actions = Object.values(cache.actions);
  const districts = actions.filter((item) => {
    if ((!item.geoObject || !item.geoObject.data
        || !item.geoObject.data.properties
        || !item.geoObject.data.properties.city_district)
      && !item.city_district) {
      return false;
    }
    return true;
  }).map((item) => {
    if (item.city_district) {
      return item.city_district;
    }
    if (item.geoObject && item.geoObject.data && item.geoObject.data.properties
      && item.geoObject.data.properties.city_district) {
      return item.geoObject.data.properties.city_district;
    }
    return false;
  });
  return [...new Set(districts)];
}

export function getActionById(actionId) {
  if (!cache.actions) {
    return undefined;
  }
  return cache.actions[actionId];
}

export function upsertAction(action) {
  if (!cache.actions) {
    cache.actions = Object.create(null);
  }
  if (!cache.actions[action.id]) {
    cache.actions[action.id] = transformers.transformAction(action);
  } else {
    cache.actions[action.id] = { ...cache.actions[action.id], ...transformers.transformAction(action) };
  }
  upsertActionToMapObjectIndex(action);
}

export function removeAction(actionId) {
  if (!cache.actions) {
    return;
  }
  if (cache.actions[actionId]) {
    const { map_object_id: mapObjectId } = cache.actions[actionId];
    if (mapObjectId && cache.actionIdsByMapObjectId[mapObjectId]) {
      cache.actionIdsByMapObjectId[mapObjectId] = cache.actionIdsByMapObjectId[mapObjectId]
        .filter(item => item.id !== actionId);
    }
    delete cache.actions[actionId];
  }
}

export function resetActions() {
  cache.actions = Object.create(null);
}

export function setMapObjects(mapObjects = Object.create(null)) {
  cache.mapObjects = mapObjects || Object.create(null);
}

export function getMapObjects() {
  return cache.mapObjects;
}

export function getMapObjectById(mapObjectId) {
  if (!cache.mapObjects) {
    return undefined;
  }
  return cache.mapObjects[mapObjectId];
}

export function upsertMapObject(mapObject) {
  if (!cache.mapObjects) {
    cache.mapObjects = Object.create(null);
  }

  if (!cache.mapObjects[mapObject.id]) {
    cache.mapObjects[mapObject.id] = transformers.transformMapObject(mapObject);
  } else {
    cache.mapObjects[mapObject.id] = { ...cache.mapObjects[mapObject.id], ...transformers.transformMapObject(mapObject) };
  }
  if (!cache.actionIdsByMapObjectId[mapObject.id]) {
    cache.actionIdsByMapObjectId[mapObject.id] = [];
  }
  if (mapObject.parent_id) {
    upsertChildToParentIndex(mapObject);
  }
}

export function removeMapObject(mapObjectId) {
  if (!cache.mapObjects) {
    return;
  }
  const mapObject = cache.mapObjects[mapObjectId];
  if (mapObject && mapObject.parent_id) {
    cache.childIdsByParentId[mapObject.parent_id] = cache.childIdsByParentId[mapObject.parent_id]
      .filter(item => item.id !== mapObjectId);
  }
  delete cache.mapObjects[mapObjectId];
}

export function resetMapObjects() {
  cache.mapObjects = Object.create(null);
}

export function getParentsForMapObjectId(mapObjectId) {
  if (!cache.mapObjects[mapObjectId] || cache.mapObjects[mapObjectId].parent_id === null) {
    return [];
  }
  let parentId = cache.mapObjects[mapObjectId].parent_id;
  const parents = [];
  while (parentId) {
    if (cache.mapObjects[parentId]) {
      parents.push(cache.mapObjects[parentId]);
      parentId = cache.mapObjects[parentId].parent_id;
    } else {
      parentId = null;
    }
  }
  return parents;
}

export function getChildrenForMapObjectId(mapObjectId) {
  const children = [];
  const childIds = cache.childIdsByParentId[mapObjectId];
  if (!childIds || childIds.length === 0) {
    return children;
  }
  childIds.forEach((childId) => {
    const child = cache.mapObjects[childId.id];
    if (child) {
      children.push(child);
    }
  });
  return children;
}

export function filterChildrenForMapObjectId(mapObjectId, property, values) {
  if (!cache.childIdsByParentId[mapObjectId] || cache.childIdsByParentId[mapObjectId].length === 0) {
    return false;
  }
  return cache.childIdsByParentId[mapObjectId].filter((item) => {
    if (values.includes(item[property])) {
      return true;
    }
    if (cache.childIdsByParentId[item.id]) {
      return cache.childIdsByParentId[item.id].filter(child => values.includes(child[property])).length > 0;
    }
    return false;
  }).length > 0;
}

export function getChildrenIdsForMapObjectId(mapObjectId, onlyDirectChildren = false) {
  if (!cache.childIdsByParentId[mapObjectId] || cache.childIdsByParentId[mapObjectId].length === 0) {
    return [];
  }
  if (onlyDirectChildren) {
    return cache.childIdsByParentId[mapObjectId].map(item => item.id);
  }
  const children = [];
  cache.childIdsByParentId[mapObjectId].forEach((childId) => {
    children.push(childId.id);
    if (cache.childIdsByParentId[childId.id]) {
      cache.childIdsByParentId[childId.id].forEach((innerChild) => {
        children.push(innerChild.id);
      });
    }
  });
  return children;
}

export function createIndexesForActionsAndMapObjects() {
  const startTime = performance.now();
  const groups = mapFunctions.groupBy(Object.values(cache.actions), 'map_object_id');
  const mapObjects = Object.values(cache.mapObjects);
  mapObjects.forEach((mapObject) => {
    const actions = groups.get(mapObject.id);
    if (actions) {
      actions.forEach((action) => {
        upsertActionToMapObjectIndex(action);
      });
    }
    if (mapObject.parent_id) {
      upsertChildToParentIndex(mapObject);
    } else if (!cache.childIdsByParentId[mapObject.id]) {
      cache.childIdsByParentId[mapObject.id] = [];
    }
  });
  const endTime = performance.now();
  console.log(`dataCache.createIndexesForActionsAndMapObjects took ${endTime - startTime} milliseconds`);
}

export function getActionsForMapObjectId(mapObjectId) {
  const startTime = performance.now();
  let actionIds = [];
  if (cache.actionIdsByMapObjectId[mapObjectId]) {
    actionIds = cache.actionIdsByMapObjectId[mapObjectId];
  }
  const actions = [];
  actionIds.forEach((item) => {
    actions.push(cache.actions[item.id]);
  });
  let childIds = [];
  const children = [];
  if (cache.childIdsByParentId[mapObjectId]) {
    childIds = cache.childIdsByParentId[mapObjectId];
  }
  childIds.forEach((item) => {
    children.push(cache.mapObjects[item.id]);
  });
  const childActions = [];
  children.forEach((child) => {
    if (cache.actionIdsByMapObjectId[child.id]) {
      cache.actionIdsByMapObjectId[mapObjectId].forEach((item) => {
        childActions.push(cache.actions[item.id]);
      });
    }
  });
  const endTime = performance.now();
  console.log(`dataCache.getOpenActionsForMapObjectId took ${endTime - startTime} milliseconds`);
  return [actions, childActions];
}

export function getOpenActionsForMapObjectId(mapObjectId, filter = {}) {
  const startTime = performance.now();
  let actionIds = [];
  if (cache.actionIdsByMapObjectId[mapObjectId]) {
    actionIds = cache.actionIdsByMapObjectId[mapObjectId]
      .filter(item => cache.openActionStatusIds.includes(item.action_status_id));
    if (filter.actionPriority && filter.actionPriority.length > 0) {
      actionIds = actionIds.filter(item => filter.actionPriority.includes(item.action_priority_id));
    }
    if (filter.actionStatus && filter.actionStatus.length > 0) {
      actionIds = actionIds.filter(item => filter.actionStatus.includes(item.action_status_id));
    }
    if (filter.dynamicActionType && filter.dynamicActionType.length > 0) {
      actionIds = actionIds.filter(item => filter.dynamicActionType.includes(item.dynamic_action_type_id));
    }
  }
  const actions = [];
  actionIds.forEach((item) => {
    actions.push(cache.actions[item.id]);
  });
  let childIds = [];
  const children = [];
  if (cache.childIdsByParentId[mapObjectId]) {
    childIds = cache.childIdsByParentId[mapObjectId];
  }
  childIds.forEach((item) => {
    if (cache.mapObjects[item.id]) {
      children.push(cache.mapObjects[item.id]);
      if (cache.childIdsByParentId[item.id]) {
        cache.childIdsByParentId[item.id].forEach((innerChild) => {
          if (cache.mapObjects[innerChild.id]) {
            children.push(cache.mapObjects[innerChild.id]);
          }
        });
      }
    }
  });
  const childActions = [];
  children.forEach((child) => {
    if (cache.actionIdsByMapObjectId[child.id]) {
      let data = cache.actionIdsByMapObjectId[child.id]
        .filter(item => cache.openActionStatusIds.includes(item.action_status_id));

      if (filter.actionPriority && filter.actionPriority.length > 0) {
        data = data.filter(item => filter.actionPriority.includes(item.action_priority_id));
      }
      if (filter.actionStatus && filter.actionStatus.length > 0) {
        data = data.filter(item => filter.actionStatus.includes(item.action_status_id));
      }
      if (filter.dynamicActionType && filter.dynamicActionType.length > 0) {
        data = data.filter(item => filter.dynamicActionType.includes(item.dynamic_action_type_id));
      }
      data.forEach((item) => {
        childActions.push(cache.actions[item.id]);
      });
    }
  });
  const endTime = performance.now();
  if ((endTime - startTime) > 1) {
    console.log(`dataCache.getOpenActionsForMapObjectId took ${endTime - startTime} milliseconds`);
  }
  return [actions, childActions];
}

export function countOpenActionsForMapObjectId(mapObjectId, filter = {}) {
  const [actions, childActions] = getOpenActionsForMapObjectId(mapObjectId, filter);
  return [actions.length, childActions.length];
}

export function getAllMapObjectIdsThatHaveActions() {
  return Object.keys(cache.actionIdsByMapObjectId);
}

export function setSelections(selections = Object.create(null)) {
  cache.selections = selections;
}

export function getSelections() {
  return cache.selections;
}

export function getSelectionById(selectionId) {
  if (!cache.selections) {
    return undefined;
  }
  return cache.selections[selectionId];
}

export function upsertSelection(selection) {
  if (!cache.selections) {
    cache.selections = Object.create(null);
  }
  cache.selections[selection.id] = selection;
}

export function removeSelection(selectionId) {
  if (!cache.selections) {
    return;
  }
  delete cache.selections[selectionId];
}

export function resetSelections() {
  cache.selections = Object.create(null);
}

export function getSelectionsForMapObjectId(mapObjectId) {
  return Object.values(cache.selections)
    .filter(selection => selection.mapObjectId.data.map(innerItem => innerItem.id).includes(mapObjectId));
}

export function getActionStatistics(dynamicActionTypes = {}) {
  const countsByTypeId = Object.values(cache.actions).reduce((data, item) => {
    const counts = data;
    const typeId = item.dynamic_action_type_id;

    counts[typeId] = (counts[typeId] || 0) + 1;

    return counts;
  }, Object.create(null));
  const statistics = [];
  Object.values(dynamicActionTypes).forEach((dynamicActionType) => {
    if (countsByTypeId[dynamicActionType.id] > 0) {
      statistics.push({
        name: dynamicActionType.name,
        amount: countsByTypeId[dynamicActionType.id],
      });
    }
  });
  return statistics;
}

export function getMapObjectStatistics(mapObjectTypes = {}) {
  const countsByTypeId = Object.values(cache.mapObjects).reduce((data, item) => {
    const counts = data;
    const typeId = item.map_object_type_id;

    counts[typeId] = (counts[typeId] || 0) + 1;

    return counts;
  }, Object.create(null));
  const statistics = [];
  Object.values(mapObjectTypes).forEach((mapObjectType) => {
    let totalLength = null;
    let totalArea = null;
    if (mapObjectType.geo_type === GeoType.linestring) {
      totalLength = Object.values(cache.mapObjects)
        .filter(item => item.map_object_type_id === mapObjectType.id)
        .reduce((data, item) => {
          if (item.length) {
            return data + parseFloat(item.length);
          }
          if (item.geoObject && item.geoObject.data && item.geoObject.data.properties && item.geoObject.data.properties.length) {
            return data + parseFloat(item.geoObject.data.properties.length);
          }
          return data + 0;
        }, 0);
    }
    if (mapObjectType.geo_type === GeoType.polygon) {
      totalArea = Object.values(cache.mapObjects)
        .filter(item => item.map_object_type_id === mapObjectType.id)
        .reduce((data, item) => {
          if (item.area) {
            return data + parseFloat(item.area);
          }
          if (item.geoObject && item.geoObject.data && item.geoObject.data.properties && item.geoObject.data.properties.area) {
            return data + parseFloat(item.geoObject.data.properties.area);
          }
          return data + 0;
        }, 0);
    }
    if (countsByTypeId[mapObjectType.id] > 0) {
      statistics.push({
        name: mapObjectType.name,
        amount: countsByTypeId[mapObjectType.id],
        length: totalLength ? (totalLength / 1000).toFixed(1) : totalLength,
        area: totalArea ? (totalArea / 1000000).toFixed(1) : totalArea,
      });
    }
  });
  return statistics;
}
